import React, { forwardRef, useState, useImperativeHandle, useRef, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { DragDropFile } from "../DragDropFile";
import { DragDropFileUpload } from "../DragDropFile/DragDropFileUpload";
import { Typography } from "@mui/material";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import CloseIcon from "@mui/icons-material/Close";
import { DocumentUpload } from "../DocumentUpload";
import { query, save } from "../../services/object";
import { getFileUrl } from "../../services/s3";

const ProjectFileUpload = forwardRef((props, ref) => {
    const [hasError, setHasError] = useState(false);
    const [masterPlanFiles, setMasterPlanFile] = useState([]);
    const [miscPlanFiles, setMiscPlanFile] = useState([]);
    const uploadDocumentRef = useRef();
    const uploadDocumentMiscRef = useRef();
    const [formValues, setFormValues] = useState(
        props.formData
            ? props.formData
            : {
                  floorPlan: [],
                  miscPlan: [],
              }
    );

    const formValueRef = useRef(
        props.formData
            ? props.formData
            : {
                  floorPlan: [],
                  miscPlan: [],
              }
    );

    useEffect(() => {
        if (formValues.id) {
            loadDocuments();
        }
    }, []);

    const loadDocuments = () => {
        query("document", "getRelatedRecords", { parentId: formValues.id }).then((result) => {
            if (result.isSuccess) {
                let documentData = result.data?.filter((item) => !item.isDeleted);
                let newFormValues = { ...formValues };
                let floorDocuments = documentData.filter((item) => item.localDocumentType === "floorPlan");
                let miscDocuments = documentData.filter((item) => item.localDocumentType === "miscPlan");

                if (!newFormValues.floorPlan) {
                    newFormValues.floorPlan = [];
                }
                if (!newFormValues.miscPlan) {
                    newFormValues.miscPlan = [];
                }

                newFormValues.floorPlan = [...newFormValues.floorPlan, ...floorDocuments];
                newFormValues.miscPlan = [...newFormValues.miscPlan, ...miscDocuments];
                setFormValues(newFormValues);
                formValueRef.current = newFormValues;
                props.onChange(newFormValues);
            }
        });
    };

    useImperativeHandle(ref, () => ({
        validate() {
            return isValidData();
        },
    }));

    const isValidData = () => {
        if (formValues?.projectDescription != "") return true;
        else return false;
    };

    const handleDownload = async (event, item) => {
        event.stopPropagation();
        event.preventDefault();
        const { isSuccess, data } = await getFileUrl({ fileId: item.file ? item.file.fileId : item.fileId });
        if (isSuccess) {
            downloadFile(data, item.name);
        }
    };

    const downloadFile = (downloadUrl, name) => {
        const downloadContainer = document.querySelector(".download-container");
        const fileName = name;

        let a = document.createElement("a");
        a.href = downloadUrl;
        a.target = "_blank";
        // Use a.download if available, it prevents plugins from opening.
        a.download = fileName;
        // Add a to the doc for click to work.
        if (downloadContainer) {
            downloadContainer.appendChild(a);
        }
        if (a.click) {
            a.click(); // The click method is supported by most browsers.
        }
        // Delete the temporary link.
        downloadContainer?.removeChild(a);
    };

    const handleDelete = async (event, document, type) => {
        event.stopPropagation();
        let newFormValues = { ...formValues };
        let documents = newFormValues[type];
        if (document.id) {
            let doc = documents?.find((item) => item.id === document.id);

            if (document) {
                if (document.parentId) {
                    documents = documents?.map((item) => {
                        if (document.id === item.id) {
                            item.isDeleted = true;
                        }
                        return item;
                    });
                } else {
                    documents = documents?.filter((item) => doc.id !== item.id);
                }
                newFormValues[type] = documents;
                setFormValues(newFormValues);
                formValueRef.current = newFormValues;
                props.onChange(newFormValues);
            }
        } else {
            let doc = documents?.find((item) => item.file?.fileId === document.file?.fileId);

            if (document) {
                if (document.parentId) {
                    documents = documents?.map((item) => {
                        if (document.file?.fileId === item.file?.fileId) {
                            item.isDeleted = true;
                        }
                        return item;
                    });
                } else {
                    documents = documents?.filter((item) => doc.file?.fileId !== item.file?.fileId);
                }
                newFormValues[type] = documents;
                setFormValues(newFormValues);
                formValueRef.current = newFormValues;
                props.onChange(newFormValues);
            }
        }
    };

    const handleFileUploadComplete = (file, documentType) => {
        let newFormValues = { ...formValueRef.current };
        if (!newFormValues[documentType]) {
            newFormValues[documentType] = [];
        }
        newFormValues[documentType] = [
            ...newFormValues[documentType],
            ...file.map((item) => {
                return { file: item, name: item.name };
            }),
        ];
        setFormValues(newFormValues);
        formValueRef.current = newFormValues;
        props.onChange(newFormValues);
    };

    return (
        <Box sx={{ pt: { xs: 2, md: 2 } }}>
            <Grid container>
                <Grid item xs={12} md={12}>
                    <Typography variant="subtitle1">Floor Plan Upload</Typography>
                    <Typography variant="caption">Upload any master floor plan of your space</Typography>
                    <Box sx={{ width: "100%", position: "relative", minHeight: "100px", marginTop: "7px" }}>
                        <DocumentUpload
                            ref={uploadDocumentRef}
                            multiple={true}
                            parentId={""}
                            id="floorPlan"
                            userDetails={{}}
                            onFileUploadComplete={handleFileUploadComplete}
                            documentType="floorPlan"></DocumentUpload>
                    </Box>

                    {formValues?.floorPlan &&
                        formValues.floorPlan.map((file) => (
                            <>
                                {!file.isDeleted && (
                                    <Chip
                                        label={file?.name}
                                        variant="outlined"
                                        onDelete={(event) => handleDelete(event, file, "floorPlan")}
                                        onClick={(event) => handleDownload(event, file)}
                                        deleteIcon={<CloseIcon sx={{ fontSize: "6px" }} />}
                                    />
                                )}
                            </>
                        ))}

                    <Divider variant="fullwidth" sx={{ mt: 1, mb: 2 }} />
                </Grid>

                <Grid item xs={12} md={12}>
                    <Typography variant="subtitle1">Misc. File Upload</Typography>
                    <Typography variant="caption">
                        Upload any other files or information that might be relevant.
                    </Typography>
                    <Box sx={{ width: "100%", position: "relative", minHeight: "100px", marginTop: "7px" }}>
                        <DocumentUpload
                            ref={uploadDocumentMiscRef}
                            id="miscPlan"
                            multiple={true}
                            parentId={""}
                            userDetails={{}}
                            onFileUploadComplete={handleFileUploadComplete}
                            documentType="miscPlan"></DocumentUpload>
                    </Box>
                    {formValues?.miscPlan &&
                        formValues.miscPlan.map((file) => (
                            <>
                                {!file.isDeleted && (
                                    <Chip
                                        label={file?.name}
                                        variant="outlined"
                                        onDelete={(event) => handleDelete(event, file, "miscPlan")}
                                        onClick={(event) => handleDownload(event, file)}
                                        deleteIcon={<CloseIcon />}
                                    />
                                )}
                            </>
                        ))}

                    <Divider variant="fullwidth" sx={{ mt: 1, mb: 2 }} />
                </Grid>
            </Grid>
            <div className="download-container"></div>
        </Box>
    );
});

export { ProjectFileUpload };
